// src/pages/teacher/TeacherDashboard.tsx
// דשבורד גננת — סיכום תקציבים + פעילות אחרונה

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, ShoppingCart, TrendingDown, Plus, Receipt, FileText, ArrowLeft } from 'lucide-react';
import { teacherApi } from '../../services/api';
import { useTeacherStore, sourceTypeNames, type BudgetSource, type Expense, type PurchaseList } from '../../stores/teacherStore';
import { useAuthStore } from '../../stores/authStore';
import { teacherColors as colors } from '../../styles/colors';

interface BudgetSummary {
  totalBudget: number;
  totalSpent: number;
  totalRemaining: number;
  sources: BudgetSource[];
  activeSourcesCount: number;
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { expenses, setExpenses, purchaseLists, setPurchaseLists, budgetSources, setBudgetSources } = useTeacherStore();

  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [sumRes, expRes, plRes] = await Promise.all([
        teacherApi.getBudgetSummary(),
        teacherApi.getExpenses(),
        teacherApi.getPurchaseLists(),
      ]);
      if (sumRes.success && sumRes.data) {
        setSummary(sumRes.data as BudgetSummary);
        if (sumRes.data.sources) setBudgetSources(sumRes.data.sources);
      }
      if (expRes.success && expRes.data) setExpenses(expRes.data);
      if (plRes.success && plRes.data) setPurchaseLists(plRes.data);
    } catch (err) { console.error('Dashboard load failed:', err); }
    finally { setLoading(false); }
  };

  const fmt = (n: number) => `${n.toLocaleString('he-IL')}₪`;
  const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('he-IL'); } catch { return s; } };

  // Budget color logic
  const getBudgetColor = (remaining: number, total: number) => {
    if (total === 0) return colors.textLight;
    const pct = remaining / total;
    if (pct > 0.5) return colors.green;
    if (pct > 0.2) return colors.yellow;
    return colors.red;
  };

  // Recent expenses (last 5)
  const recentExpenses = [...expenses]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Active purchase lists (not invoiced)
  const activeLists = purchaseLists.filter(pl => pl.status < 3).slice(0, 3);

  // Current month expenses total
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const thisMonthTotal = expenses
    .filter(e => e.expenseDate >= monthStart.slice(0, 10))
    .reduce((s, e) => s + e.amount, 0);

  const totalRemaining = summary?.totalRemaining ?? 0;
  const totalBudget = summary?.totalBudget ?? 0;
  const budgetColor = getBudgetColor(totalRemaining, totalBudget);

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      {/* Welcome */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
          שלום, {user?.firstName || user?.name || 'גננת'}
        </h1>
        <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
          {user?.kindergartenName || 'גן ילדים'}
        </p>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>טוען...</div>}

      {!loading && (
        <>
          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div onClick={() => navigate('/teacher/budgets')} style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '16px', border: `1px solid ${colors.border}`, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: budgetColor === colors.green ? colors.greenLight : budgetColor === colors.yellow ? colors.yellowLight : colors.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Wallet size={18} color={budgetColor} />
                </div>
                <span style={{ fontSize: '12px', color: colors.textLight }}>תקציב זמין</span>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 'bold', color: budgetColor, margin: 0 }}>
                {fmt(totalRemaining)}
              </p>
              {totalBudget > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ height: '4px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, totalBudget > 0 ? ((totalBudget - totalRemaining) / totalBudget) * 100 : 0)}%`, backgroundColor: budgetColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                  </div>
                  <p style={{ fontSize: '10px', color: colors.textLight, marginTop: '4px' }}>
                    {fmt(summary?.totalSpent ?? 0)} מתוך {fmt(totalBudget)}
                  </p>
                </div>
              )}
            </div>

            <div onClick={() => navigate('/teacher/expenses')} style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '16px', border: `1px solid ${colors.border}`, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: colors.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingDown size={18} color={colors.red} />
                </div>
                <span style={{ fontSize: '12px', color: colors.textLight }}>הוצאות החודש</span>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
                {fmt(thisMonthTotal)}
              </p>
              <p style={{ fontSize: '10px', color: colors.textLight, marginTop: '10px' }}>
                {expenses.length} הוצאות סה״כ
              </p>
            </div>
          </div>

          {/* Budget sources mini-cards */}
          {summary && summary.sources.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, margin: 0 }}>מקורות תקציב</h2>
                <button onClick={() => navigate('/teacher/budgets')} style={{ fontSize: '12px', color: colors.primary, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '500' }}>הכל &larr;</button>
              </div>
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                {summary.sources.filter(s => s.isActive).slice(0, 4).map(src => {
                  const srcColor = getBudgetColor(src.remainingBudget, src.totalBudget);
                  const pct = src.totalBudget > 0 ? (src.totalSpent / src.totalBudget) * 100 : 0;
                  return (
                    <div key={src.publicId} style={{ minWidth: '140px', backgroundColor: colors.white, borderRadius: '10px', padding: '12px', border: `1px solid ${colors.border}`, flexShrink: 0 }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: colors.text, margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{src.name}</p>
                      <p style={{ fontSize: '10px', color: colors.textLight, margin: '0 0 8px' }}>{sourceTypeNames[src.sourceType]}</p>
                      <div style={{ height: '3px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden', marginBottom: '4px' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, backgroundColor: srcColor, borderRadius: '2px' }} />
                      </div>
                      <p style={{ fontSize: '11px', fontWeight: '600', color: srcColor, margin: 0 }}>{fmt(src.remainingBudget)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '10px' }}>
              פעולות מהירות
            </h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => navigate('/teacher/purchases')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', backgroundColor: colors.primaryLight, border: `1px solid ${colors.primary}40`, borderRadius: '12px', cursor: 'pointer', color: colors.primary, fontSize: '14px', fontWeight: '500' }}>
                <ShoppingCart size={18} />
                רשימת קניות
              </button>
              <button onClick={() => navigate('/teacher/invoices')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', backgroundColor: colors.primaryLight, border: `1px solid ${colors.primary}40`, borderRadius: '12px', cursor: 'pointer', color: colors.primary, fontSize: '14px', fontWeight: '500' }}>
                <Receipt size={18} />
                סרקי חשבונית
              </button>
            </div>
          </div>

          {/* Active Purchase Lists */}
          {activeLists.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, margin: 0 }}>רשימות פעילות</h2>
                <button onClick={() => navigate('/teacher/purchases')} style={{ fontSize: '12px', color: colors.primary, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '500' }}>הכל &larr;</button>
              </div>
              {activeLists.map(pl => (
                <div key={pl.publicId} onClick={() => navigate('/teacher/purchases')} style={{ backgroundColor: colors.white, borderRadius: '10px', border: `1px solid ${colors.border}`, padding: '12px 14px', marginBottom: '8px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>{pl.name}</span>
                      <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '2px' }}>
                        {pl.purchasedItemCount}/{pl.itemCount} נרכשו
                      </div>
                    </div>
                    {pl.totalEstimated != null && pl.totalEstimated > 0 && (
                      <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>{fmt(pl.totalEstimated)}</span>
                    )}
                  </div>
                  {pl.itemCount > 0 && (
                    <div style={{ marginTop: '6px', height: '3px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(pl.purchasedItemCount / pl.itemCount) * 100}%`, backgroundColor: colors.green, borderRadius: '2px' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Recent Expenses */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, margin: 0 }}>הוצאות אחרונות</h2>
              {expenses.length > 0 && (
                <button onClick={() => navigate('/teacher/expenses')} style={{ fontSize: '12px', color: colors.primary, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '500' }}>הכל &larr;</button>
              )}
            </div>
            {recentExpenses.length === 0 && (
              <div style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '32px 24px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: colors.textLight }}>אין פעילות עדיין</p>
                <p style={{ fontSize: '12px', color: colors.textLight, marginTop: '4px' }}>התחילי להוסיף תקציבים ורשימות קניות</p>
              </div>
            )}
            {recentExpenses.map(exp => (
              <div key={exp.publicId} style={{ backgroundColor: colors.white, borderRadius: '10px', border: `1px solid ${colors.border}`, padding: '10px 14px', marginBottom: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>{exp.description}</span>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: colors.textLight, marginTop: '2px' }}>
                      <span>{fmtDate(exp.expenseDate)}</span>
                      <span>{exp.budgetSourceName}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{fmt(exp.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
